import React, { useState, useEffect, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { operadorasService, planosService, catalogoPlanosService } from '../services/api';
import { maskMoney, unmaskMoney } from '../utils/masks';
import '../styles/Planos.css';

const emptyPfNeoForm = () => ({
  titulo: '',
  descricao: '',
  ordem: 0,
  ativo: true,
  nome_neo: '',
  valor_padrao: '',
  filtro_busca: '',
  categoria: 'fibra'
});

const Planos = () => {
  const navigate = useNavigate();
  const [operadoras, setOperadoras] = useState([]);
  const [loading, setLoading] = useState(true);
  const [planosPorOperadora, setPlanosPorOperadora] = useState({});
  const [expandidoId, setExpandidoId] = useState(null);
  const [novoPlano, setNovoPlano] = useState({ nome: '', valor: '' });
  const [editandoPlano, setEditandoPlano] = useState(null);
  const [salvando, setSalvando] = useState(false);
  const [produtosPfNeo, setProdutosPfNeo] = useState([]);
  const [loadingPfNeo, setLoadingPfNeo] = useState(true);
  const [buscaPfNeo, setBuscaPfNeo] = useState('');
  const [showPfNeoForm, setShowPfNeoForm] = useState(false);
  const [editingPfNeoId, setEditingPfNeoId] = useState(null);
  const [pfNeoForm, setPfNeoForm] = useState(emptyPfNeoForm());
  const [pfNeoFormError, setPfNeoFormError] = useState('');
  const [deletingPfNeoId, setDeletingPfNeoId] = useState(null);
  const [pfNeoPage, setPfNeoPage] = useState(1);
  const [pfNeoItemsPerPage, setPfNeoItemsPerPage] = useState(20);

  useEffect(() => {
    loadOperadoras();
    loadProdutosPfNeo();
  }, []);

  useEffect(() => {
    setPfNeoPage(1);
  }, [buscaPfNeo, pfNeoItemsPerPage]);

  const produtosPfNeoFiltrados = useMemo(() => {
    const q = buscaPfNeo.trim().toLowerCase();
    if (!q) return produtosPfNeo;
    return produtosPfNeo.filter((item) =>
      (item.titulo || '').toLowerCase().includes(q) ||
      (item.nome_neo || '').toLowerCase().includes(q) ||
      (item.filtro_busca || '').toLowerCase().includes(q) ||
      (item.categoria || '').toLowerCase().includes(q)
    );
  }, [produtosPfNeo, buscaPfNeo]);

  const pfNeoTotal = produtosPfNeoFiltrados.length;
  const pfNeoTotalPages = Math.max(1, Math.ceil(pfNeoTotal / pfNeoItemsPerPage));

  const produtosPfNeoPaginados = useMemo(() => {
    const start = (pfNeoPage - 1) * pfNeoItemsPerPage;
    return produtosPfNeoFiltrados.slice(start, start + pfNeoItemsPerPage);
  }, [produtosPfNeoFiltrados, pfNeoPage, pfNeoItemsPerPage]);

  useEffect(() => {
    if (pfNeoPage > pfNeoTotalPages) {
      setPfNeoPage(pfNeoTotalPages);
    }
  }, [pfNeoPage, pfNeoTotalPages]);

  const loadProdutosPfNeo = async () => {
    try {
      setLoadingPfNeo(true);
      const data = await catalogoPlanosService.listPfNeo();
      setProdutosPfNeo(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Erro ao carregar produtos PF NEO:', error);
      setProdutosPfNeo([]);
    } finally {
      setLoadingPfNeo(false);
    }
  };

  const loadOperadoras = async () => {
    try {
      setLoading(true);
      const data = await operadorasService.getAll();
      setOperadoras(data);
    } catch (error) {
      console.error('Erro ao carregar operadoras:', error);
      alert('Erro ao carregar operadoras');
    } finally {
      setLoading(false);
    }
  };

  const loadPlanos = async (operadoraId) => {
    try {
      const data = await planosService.getByOperadora(operadoraId);
      setPlanosPorOperadora(prev => ({ ...prev, [operadoraId]: data }));
    } catch (error) {
      console.error('Erro ao carregar planos:', error);
      setPlanosPorOperadora(prev => ({ ...prev, [operadoraId]: [] }));
    }
  };

  const handleTogglePlanosHabilitados = async (operadora) => {
    const novoValor = !operadora.planos_habilitados;
    try {
      setSalvando(true);
      await operadorasService.update(operadora.id, { planos_habilitados: novoValor });
      setOperadoras(prev =>
        prev.map(op => (op.id === operadora.id ? { ...op, planos_habilitados: novoValor } : op))
      );
      if (novoValor) {
        await loadPlanos(operadora.id);
        setExpandidoId(operadora.id);
      } else {
        setExpandidoId(null);
      }
    } catch (error) {
      console.error('Erro ao atualizar operadora:', error);
      alert('Erro ao atualizar: ' + (error.response?.data?.error || 'Erro desconhecido'));
    } finally {
      setSalvando(false);
    }
  };

  const handleExpandir = (operadoraId) => {
    if (expandidoId === operadoraId) {
      setExpandidoId(null);
      setNovoPlano({ nome: '', valor: '' });
      setEditandoPlano(null);
      return;
    }
    setExpandidoId(operadoraId);
    setNovoPlano({ nome: '', valor: '' });
    setEditandoPlano(null);
    if (!planosPorOperadora[operadoraId]) {
      loadPlanos(operadoraId);
    }
  };

  const handleAdicionarPlano = async (e, operadoraId) => {
    e.preventDefault();
    if (!novoPlano.nome.trim()) {
      alert('Informe o nome do plano');
      return;
    }
    const valorStr = unmaskMoney(novoPlano.valor);
    const valorNum = parseFloat(valorStr);
    if (valorStr === '' || isNaN(valorNum) || valorNum < 0) {
      alert('Informe um valor válido');
      return;
    }
    try {
      setSalvando(true);
      await planosService.create({
        operadora_id: operadoraId,
        nome: novoPlano.nome.trim(),
        valor: valorNum
      });
      setNovoPlano({ nome: '', valor: '' });
      await loadPlanos(operadoraId);
    } catch (error) {
      console.error('Erro ao criar plano:', error);
      alert('Erro ao criar plano: ' + (error.response?.data?.error || 'Erro desconhecido'));
    } finally {
      setSalvando(false);
    }
  };

  const handleEditarPlano = (plano) => {
    setEditandoPlano(plano);
  };

  const handleSalvarEdicaoPlano = async (operadoraId) => {
    if (!editandoPlano) return;
    if (!editandoPlano.nome.trim()) {
      alert('Nome do plano não pode ser vazio');
      return;
    }
    const valorNum = typeof editandoPlano.valor === 'string'
      ? unmaskMoney(editandoPlano.valor)
      : parseFloat(editandoPlano.valor);
    if (valorNum === null || isNaN(valorNum) || valorNum < 0) {
      alert('Informe um valor válido');
      return;
    }
    try {
      setSalvando(true);
      await planosService.update(editandoPlano.id, {
        nome: editandoPlano.nome.trim(),
        valor: valorNum
      });
      setEditandoPlano(null);
      await loadPlanos(operadoraId);
    } catch (error) {
      console.error('Erro ao atualizar plano:', error);
      alert('Erro ao atualizar plano: ' + (error.response?.data?.error || 'Erro desconhecido'));
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluirPlano = async (plano, operadoraId) => {
    if (!window.confirm(`Excluir o plano "${plano.nome}"?`)) return;
    try {
      setSalvando(true);
      await planosService.delete(plano.id);
      await loadPlanos(operadoraId);
    } catch (error) {
      console.error('Erro ao excluir plano:', error);
      alert('Erro ao excluir plano: ' + (error.response?.data?.error || 'Erro desconhecido'));
    } finally {
      setSalvando(false);
    }
  };

  const formatValor = (v) => {
    if (v == null) return '';
    const n = typeof v === 'string' ? parseFloat(v) : v;
    return isNaN(n) ? '' : maskMoney(String(n.toFixed(2)));
  };

  const openNovoPfNeo = () => {
    setEditingPfNeoId(null);
    setPfNeoForm(emptyPfNeoForm());
    setPfNeoFormError('');
    setShowPfNeoForm(true);
  };

  const openEditPfNeo = (item) => {
    setEditingPfNeoId(item.id);
    setPfNeoForm({
      titulo: item.titulo,
      descricao: item.descricao || '',
      ordem: item.ordem || 0,
      ativo: item.ativo !== false,
      nome_neo: item.nome_neo || '',
      valor_padrao: item.valor_padrao != null ? String(item.valor_padrao) : '',
      filtro_busca: item.filtro_busca || '',
      categoria: item.categoria || 'fibra'
    });
    setPfNeoFormError('');
    setShowPfNeoForm(true);
  };

  const closePfNeoForm = () => {
    setShowPfNeoForm(false);
    setEditingPfNeoId(null);
    setPfNeoFormError('');
  };

  const handlePfNeoChange = (field, value) => {
    setPfNeoForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSubmitPfNeo = async (e) => {
    e.preventDefault();
    setPfNeoFormError('');
    if (!pfNeoForm.titulo.trim()) {
      setPfNeoFormError('Título é obrigatório.');
      return;
    }
    if (!pfNeoForm.nome_neo.trim()) {
      setPfNeoFormError('Nome NEO é obrigatório.');
      return;
    }
    const payload = {
      titulo: pfNeoForm.titulo.trim(),
      descricao: pfNeoForm.descricao.trim() || null,
      ordem: pfNeoForm.ordem,
      ativo: pfNeoForm.ativo,
      nome_neo: pfNeoForm.nome_neo.trim(),
      valor_padrao: pfNeoForm.valor_padrao !== '' ? pfNeoForm.valor_padrao : null,
      filtro_busca: pfNeoForm.filtro_busca.trim() || null,
      categoria: pfNeoForm.categoria || null,
      tipo_selecao: 'pf_neo'
    };
    try {
      setSalvando(true);
      if (editingPfNeoId) {
        await catalogoPlanosService.update(editingPfNeoId, payload);
      } else {
        await catalogoPlanosService.create(payload);
      }
      closePfNeoForm();
      await loadProdutosPfNeo();
    } catch (error) {
      setPfNeoFormError(error.response?.data?.error || 'Erro ao salvar produto PF NEO.');
    } finally {
      setSalvando(false);
    }
  };

  const handleExcluirPfNeo = async (item) => {
    if (!window.confirm(`Excluir o produto "${item.titulo}"?`)) return;
    try {
      setDeletingPfNeoId(item.id);
      setSalvando(true);
      await catalogoPlanosService.delete(item.id);
      await loadProdutosPfNeo();
    } catch (error) {
      alert(error.response?.data?.error || 'Erro ao excluir produto');
    } finally {
      setDeletingPfNeoId(null);
      setSalvando(false);
    }
  };

  return (
    <Layout>
      <div className="planos-page">
        <div className="planos-header">
          <div>
            <h1 className="planos-title">📋 Planos por Operadora</h1>
            <p className="planos-subtitle">
              Ative os planos por operadora e cadastre nome e valor. Na Nova Venda, o vendedor poderá escolher o plano e o valor será preenchido automaticamente.
            </p>
          </div>
          <div className="header-actions">
            <button onClick={() => navigate('/dashboard')} className="btn btn-secondary">
              🏠 Voltar ao Dashboard
            </button>
          </div>
        </div>

        {loading ? (
          <div className="loading">Carregando operadoras...</div>
        ) : operadoras.length === 0 ? (
          <div className="planos-empty">
            <p>Nenhuma operadora cadastrada. As operadoras são criadas ao consultar viabilidade.</p>
          </div>
        ) : (
          <div className="planos-list">
            {operadoras.map((operadora) => (
              <div key={operadora.id} className="planos-card">
                <div className="planos-card-header">
                  <div className="planos-card-titulo">
                    <span className="operadora-nome">
                      {operadora.nome_display || (operadora.nome && operadora.nome.charAt(0).toUpperCase() + operadora.nome.slice(1)) || operadora.nome}
                    </span>
                    {operadora.planos_habilitados && (
                      <span className="badge-ativo">Planos ativos</span>
                    )}
                  </div>
                  <div className="planos-card-actions">
                    <label className="toggle-planos">
                      <input
                        type="checkbox"
                        checked={!!operadora.planos_habilitados}
                        onChange={() => handleTogglePlanosHabilitados(operadora)}
                        disabled={salvando}
                      />
                      <span>Ativar planos</span>
                    </label>
                    {operadora.planos_habilitados && (
                      <button
                        type="button"
                        className="btn btn-small"
                        onClick={() => handleExpandir(operadora.id)}
                      >
                        {expandidoId === operadora.id ? 'Ocultar planos' : 'Gerenciar planos'}
                      </button>
                    )}
                  </div>
                </div>

                {operadora.planos_habilitados && expandidoId === operadora.id && (
                  <div className="planos-card-body">
                    <form
                      className="form-novo-plano"
                      onSubmit={(e) => handleAdicionarPlano(e, operadora.id)}
                    >
                      <input
                        type="text"
                        placeholder="Nome do plano (ex: 500 mega)"
                        value={novoPlano.nome}
                        onChange={(e) => setNovoPlano(prev => ({ ...prev, nome: e.target.value }))}
                        className="input-nome"
                      />
                      <input
                        type="text"
                        placeholder="Valor (ex: 99,99)"
                        value={novoPlano.valor}
                        onChange={(e) => setNovoPlano(prev => ({ ...prev, valor: maskMoney(e.target.value) }))}
                        className="input-valor"
                      />
                      <button type="submit" className="btn btn-primary" disabled={salvando}>
                        ➕ Adicionar
                      </button>
                    </form>

                    <div className="planos-tabela-wrapper">
                      {!planosPorOperadora[operadora.id] ? (
                        <div className="loading-inline">Carregando planos...</div>
                      ) : planosPorOperadora[operadora.id].length === 0 ? (
                        <p className="sem-planos">Nenhum plano cadastrado. Adicione acima.</p>
                      ) : (
                        <table className="planos-tabela">
                          <thead>
                            <tr>
                              <th>Plano</th>
                              <th>Valor (R$)</th>
                              <th>Ações</th>
                            </tr>
                          </thead>
                          <tbody>
                            {planosPorOperadora[operadora.id].map((plano) => (
                              <tr key={plano.id}>
                                {editandoPlano?.id === plano.id ? (
                                  <>
                                    <td>
                                      <input
                                        type="text"
                                        value={editandoPlano.nome}
                                        onChange={(e) => setEditandoPlano(prev => ({ ...prev, nome: e.target.value }))}
                                        className="input-edit"
                                      />
                                    </td>
                                    <td>
                                      <input
                                        type="text"
                                        value={typeof editandoPlano.valor === 'string' ? editandoPlano.valor : formatValor(editandoPlano.valor)}
                                        onChange={(e) => setEditandoPlano(prev => ({ ...prev, valor: e.target.value }))}
                                        className="input-edit input-valor-edit"
                                      />
                                    </td>
                                    <td>
                                      <button
                                        type="button"
                                        className="btn btn-small btn-primary"
                                        onClick={() => handleSalvarEdicaoPlano(operadora.id)}
                                        disabled={salvando}
                                      >
                                        Salvar
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-small btn-secondary"
                                        onClick={() => setEditandoPlano(null)}
                                      >
                                        Cancelar
                                      </button>
                                    </td>
                                  </>
                                ) : (
                                  <>
                                    <td>{plano.nome}</td>
                                    <td>{formatValor(plano.valor)}</td>
                                    <td>
                                      <button
                                        type="button"
                                        className="btn btn-small"
                                        onClick={() => handleEditarPlano({ ...plano, valor: formatValor(plano.valor) })}
                                        disabled={!!editandoPlano}
                                      >
                                        Editar
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-small btn-danger"
                                        onClick={() => handleExcluirPlano(plano, operadora.id)}
                                        disabled={salvando}
                                      >
                                        Excluir
                                      </button>
                                    </td>
                                  </>
                                )}
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      )}
                    </div>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <section className="planos-pf-neo-section">
          <div className="planos-pf-neo-header">
            <div>
              <h2 className="planos-pf-neo-title">📦 Produtos PF NEO</h2>
              <p className="planos-pf-neo-subtitle">
                Catálogo usado na Nova Venda CPF e na automação NEO. Cadastre o nome exato da combo do NEO SALES.
              </p>
            </div>
            <button type="button" className="btn btn-primary" onClick={openNovoPfNeo} disabled={salvando}>
              ➕ Novo produto PF NEO
            </button>
          </div>

          <input
            type="text"
            className="planos-pf-neo-search"
            value={buscaPfNeo}
            onChange={(e) => setBuscaPfNeo(e.target.value)}
            placeholder="Buscar por título, nome NEO, categoria..."
          />

          {!loadingPfNeo && pfNeoTotal > 0 && (
            <div className="planos-pf-neo-pagination-info">
              <span>
                Mostrando {((pfNeoPage - 1) * pfNeoItemsPerPage) + 1} a{' '}
                {Math.min(pfNeoPage * pfNeoItemsPerPage, pfNeoTotal)} de {pfNeoTotal} produtos
              </span>
              <label className="planos-pf-neo-per-page">
                Por página:
                <select
                  value={pfNeoItemsPerPage}
                  onChange={(e) => setPfNeoItemsPerPage(Number(e.target.value))}
                >
                  <option value={10}>10</option>
                  <option value={20}>20</option>
                  <option value={50}>50</option>
                  <option value={100}>100</option>
                </select>
              </label>
            </div>
          )}

          {loadingPfNeo ? (
            <div className="loading-inline">Carregando produtos PF NEO...</div>
          ) : produtosPfNeoFiltrados.length === 0 ? (
            <p className="sem-planos">Nenhum produto PF NEO encontrado.</p>
          ) : (
            <div className="planos-tabela-wrapper">
              <table className="planos-tabela planos-pf-neo-table">
                <thead>
                  <tr>
                    <th>Título</th>
                    <th>Nome NEO</th>
                    <th>Valor padrão</th>
                    <th>Categoria</th>
                    <th>Filtro</th>
                    <th>Ordem</th>
                    <th>Status</th>
                    <th>Ações</th>
                  </tr>
                </thead>
                <tbody>
                  {produtosPfNeoPaginados.map((item) => (
                    <tr key={item.id} className={item.ativo === false ? 'planos-pf-neo-inativo' : ''}>
                      <td>{item.titulo}</td>
                      <td>{item.nome_neo || '—'}</td>
                      <td>
                        {item.valor_padrao != null
                          ? formatValor(item.valor_padrao)
                          : '—'}
                      </td>
                      <td>{item.categoria || '—'}</td>
                      <td>{item.filtro_busca || '—'}</td>
                      <td>{item.ordem ?? 0}</td>
                      <td>{item.ativo === false ? 'Inativo' : 'Ativo'}</td>
                      <td>
                        <button
                          type="button"
                          className="btn btn-small"
                          onClick={() => openEditPfNeo(item)}
                          disabled={salvando}
                        >
                          Editar
                        </button>
                        <button
                          type="button"
                          className="btn btn-small btn-danger"
                          onClick={() => handleExcluirPfNeo(item)}
                          disabled={salvando || deletingPfNeoId === item.id}
                        >
                          {deletingPfNeoId === item.id ? '...' : 'Excluir'}
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!loadingPfNeo && pfNeoTotal > 0 && pfNeoTotalPages > 1 && (
            <div className="planos-pf-neo-pagination">
              <button
                type="button"
                className="btn btn-small"
                onClick={() => setPfNeoPage(1)}
                disabled={pfNeoPage === 1}
              >
                «
              </button>
              <button
                type="button"
                className="btn btn-small"
                onClick={() => setPfNeoPage((p) => Math.max(1, p - 1))}
                disabled={pfNeoPage === 1}
              >
                Anterior
              </button>
              <span className="planos-pf-neo-page-label">
                Página {pfNeoPage} de {pfNeoTotalPages}
              </span>
              <button
                type="button"
                className="btn btn-small"
                onClick={() => setPfNeoPage((p) => Math.min(pfNeoTotalPages, p + 1))}
                disabled={pfNeoPage === pfNeoTotalPages}
              >
                Próxima
              </button>
              <button
                type="button"
                className="btn btn-small"
                onClick={() => setPfNeoPage(pfNeoTotalPages)}
                disabled={pfNeoPage === pfNeoTotalPages}
              >
                »
              </button>
            </div>
          )}
        </section>

        {showPfNeoForm && (
          <div className="modal-overlay" onClick={closePfNeoForm}>
            <div className="modal-content planos-pf-neo-modal" onClick={(e) => e.stopPropagation()}>
              <div className="modal-header-venda">
                <h2>{editingPfNeoId ? 'Editar produto PF NEO' : 'Novo produto PF NEO'}</h2>
                <span className="close-modal" onClick={closePfNeoForm}>×</span>
              </div>
              <form onSubmit={handleSubmitPfNeo} className="modal-body-venda">
                {pfNeoFormError && <p className="planos-pf-neo-form-error">{pfNeoFormError}</p>}
                <div className="planos-pf-neo-form-grid">
                  <div className="form-group">
                    <label>Título (rótulo amigável) *</label>
                    <input
                      type="text"
                      value={pfNeoForm.titulo}
                      onChange={(e) => handlePfNeoChange('titulo', e.target.value)}
                      className="input-edit"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Nome NEO (texto exato da combo) *</label>
                    <input
                      type="text"
                      value={pfNeoForm.nome_neo}
                      onChange={(e) => handlePfNeoChange('nome_neo', e.target.value)}
                      className="input-edit"
                      placeholder="PF - FIBRA 600 MEGA R$ 120,00"
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Valor padrão (R$)</label>
                    <input
                      type="text"
                      value={pfNeoForm.valor_padrao}
                      onChange={(e) => handlePfNeoChange('valor_padrao', e.target.value)}
                      className="input-edit"
                      placeholder="120,00"
                    />
                  </div>
                  <div className="form-group">
                    <label>Filtro busca NEO</label>
                    <input
                      type="text"
                      value={pfNeoForm.filtro_busca}
                      onChange={(e) => handlePfNeoChange('filtro_busca', e.target.value)}
                      className="input-edit"
                      placeholder="600 MEGA"
                    />
                  </div>
                  <div className="form-group">
                    <label>Categoria</label>
                    <select
                      value={pfNeoForm.categoria}
                      onChange={(e) => handlePfNeoChange('categoria', e.target.value)}
                      className="input-edit"
                    >
                      <option value="fibra">Fibra</option>
                      <option value="streaming">Streaming</option>
                      <option value="movel">Móvel</option>
                      <option value="vivo_total">Vivo Total</option>
                      <option value="bl_avulsa">BL avulsa</option>
                      <option value="outro">Outro</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Ordem</label>
                    <input
                      type="number"
                      min="0"
                      value={pfNeoForm.ordem}
                      onChange={(e) => handlePfNeoChange('ordem', parseInt(e.target.value, 10) || 0)}
                      className="input-edit"
                    />
                  </div>
                  <div className="form-group form-group-checkbox">
                    <label>
                      <input
                        type="checkbox"
                        checked={pfNeoForm.ativo}
                        onChange={(e) => handlePfNeoChange('ativo', e.target.checked)}
                      />
                      Ativo
                    </label>
                  </div>
                </div>
                <div className="form-group">
                  <label>Descrição</label>
                  <textarea
                    value={pfNeoForm.descricao}
                    onChange={(e) => handlePfNeoChange('descricao', e.target.value)}
                    className="input-edit"
                    rows={3}
                  />
                </div>
                <div className="planos-pf-neo-modal-actions">
                  <button type="button" className="btn btn-secondary" onClick={closePfNeoForm} disabled={salvando}>
                    Cancelar
                  </button>
                  <button type="submit" className="btn btn-primary" disabled={salvando}>
                    {salvando ? 'Salvando...' : 'Salvar'}
                  </button>
                </div>
              </form>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Planos;
